import React, { useState } from "react";
import styled from "styled-components";
import { PlannerDynamicSidebarContentType } from "@lifeat/shared/planner/PlannerTypes";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { useUserContext } from "@contexts/UserContext";
import { useSignalEffect } from "@preact/signals-react";
import CalendarSidebar from "../../../calendar/components/CalendarSidebar";
import { widgetData } from "../../../widget-workspace/WidgetDataManager";
import NotesWidget from "../../../widget-workspace/components/Widget/NotesWidget/NotesWidget";
import TaskListWidget from "../../../widget-workspace/components/Widget/TaskListWidget/TaskListWidget";
import { PlannerSidebarTaskListFilterService } from "../../PlannerSidebarTaskListFilterService";
import { selectedDetail$ } from "../../TaskManagementGlobals";
import useTaskData from "../../useTaskData";
import { dynamicSidebarContent$ } from "../PlannerGlobals";

const PlannerDynamicSidebarContainer = styled.div`
    height: 100%;
    width: fit-content;
    border-left: 1px solid ${(props) => props.theme.borderPrimary};
`;

const DynamicElementWrapper = styled.div<{ isOpen: boolean }>`
    display: ${(props) => (props.isOpen ? "block" : "none")};
    height: 100%;
    width: 340px;
    min-width: inherit;
`;

const StyledTaskList = styled(TaskListWidget)`
    height: 100% !important;
    width: 100% !important;
    position: unset;
    min-height: 100% !important;
    min-width: unset !important;
    max-width: unset !important;
    max-height: unset !important;
    border-radius: unset;

    .widget-header {
        cursor: unset;
    }
`;

const StyledCalendar = styled(CalendarSidebar)`
    width: 100%;
    border-radius: unset;
    min-width: unset;

    .rbc-calendar[style] {
        width: inherit !important;
    }
`;

const StyledNotes = styled(NotesWidget)`
    height: 100% !important;
    width: 100% !important;
    position: unset;
    min-height: 100% !important;
    min-width: unset !important;
    max-height: unset !important;
    border-radius: unset;

    .widget-header {
        cursor: unset;
    }

    .notes-widget-toolbar {
        padding: 8px;
    }
`;

export default function PlannerDynamicSidebar() {
    const { lightMode } = useAppearanceContext();
    const { user } = useUserContext();

    const [prevSidebarContent, setPrevSidebarContent] = useState<PlannerDynamicSidebarContentType | null>(
        dynamicSidebarContent$.value,
    );

    const taskData = useTaskData({
        uid: user?.uid,
        filterService: PlannerSidebarTaskListFilterService(user?.uid),
    });
    /**
     * This hook is used to set the dynamic sidebar content to the correct value
     * if it was changed as part of some other action.
     * E.g. clicking on a task event from the calendar day view will open the
     * task detail view. When the task detail view is closed, the sidebar should
     * return to displaying the calendar day view.
     * TODO: remove this as part of LAS-1544
     */
    useSignalEffect(() => {
        if (selectedDetail$.value) {
            dynamicSidebarContent$.value = "task-list";
            if (prevSidebarContent == null) {
                setPrevSidebarContent(dynamicSidebarContent$.value);
            }
        } else {
            if (prevSidebarContent) {
                dynamicSidebarContent$.value = prevSidebarContent ?? "none";
                setPrevSidebarContent(null);
            }
        }
    });

    return (
        <PlannerDynamicSidebarContainer>
            <DynamicElementWrapper isOpen={dynamicSidebarContent$.value === "task-list"}>
                <StyledTaskList
                    {...widgetData.TaskListWidget}
                    taskData={taskData}
                    amplitudeEventSource="plan-mode"
                    isUserAuthenticated={!!user}
                    isOpen={true}
                    isStatic={true}
                    lightMode={lightMode}
                    isResizable={false}
                    footerAutoHide={false}
                    closeWidget={() => {
                        dynamicSidebarContent$.value = "none";
                        selectedDetail$.value = null;
                    }}
                />
            </DynamicElementWrapper>

            <DynamicElementWrapper isOpen={dynamicSidebarContent$.value === "calendar-day"}>
                <StyledCalendar
                    show={dynamicSidebarContent$.value === "calendar-day"}
                    amplitudeEventSource="plan-mode"
                />
            </DynamicElementWrapper>

            <DynamicElementWrapper isOpen={dynamicSidebarContent$.value === "notes"}>
                <StyledNotes
                    {...widgetData.NotesWidget}
                    amplitudeEventSource="plan-mode"
                    isUserAuthenticated={!!user}
                    isOpen={true}
                    isStatic={true}
                    lightMode={lightMode}
                    isResizable={false}
                    footerAutoHide={false}
                    closeWidget={() => {
                        dynamicSidebarContent$.value = "none";
                        selectedDetail$.value = null;
                    }}
                />
            </DynamicElementWrapper>
        </PlannerDynamicSidebarContainer>
    );
}
